import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, Sparkles, ShieldCheck, Zap } from 'lucide-react';
import { useState } from 'react';
import Seo from '@/components/Seo';
import { ToolCard } from '@/components/tools/ToolCard';
import { BlogCard } from '@/components/blog/BlogCard';
import { AdSlot } from '@/components/ads/AdSlot';
import { CATEGORIES, TOOLS, TRUST_POINTS } from '@/lib/tools';
import { getBlogPosts } from '@/lib/api';
import { useEffect } from 'react';
import { HOME_PAGE } from '@/constants/testIds';

export default function HomePage() {
    const [q, setQ] = useState('');
    const [posts, setPosts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        getBlogPosts()
            .then((data) => setPosts(data.slice(0, 3)))
            .catch(() => setPosts([]));
    }, []);

    const featured = TOOLS.filter((t) => t.status === 'live').slice(0, 6);
    const nextUp = TOOLS.filter((t) => t.status !== 'live').slice(0, 3);

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'UtilityTool',
        url: '/',
        potentialAction: {
            '@type': 'SearchAction',
            target: '/tools?q={search_term_string}',
            'query-input': 'required name=search_term_string',
        },
    };

    return (
        <>
            <Seo
                title="Fast, private online utility tools"
                description="UtilityTool is a growing collection of small, focused web utilities — Word to PDF, JPG to PNG, image compression and more. Free, no signup."
                canonical="/"
                structuredData={structured}
            />

            {/* Hero */}
            <section
                data-testid={HOME_PAGE.hero}
                className="relative overflow-hidden noise-overlay"
            >
                <div
                    aria-hidden
                    className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_0%,hsl(214_95%_93%)_0%,transparent_60%)]"
                />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 lg:pt-24 pb-14">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-border/80 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
                                <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                                Now with Word→PDF, JPG→PNG & Image Compressor
                            </div>
                            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
                                Small tools that <span className="text-[hsl(var(--primary))]">just work.</span>
                            </h1>
                            <p className="mt-5 text-lg text-slate-600 max-w-xl leading-relaxed">
                                Convert documents, shrink images, and tick off small
                                everyday tasks. No signup, no popups — just fast, private
                                utilities you can bookmark and share.
                            </p>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    navigate(`/tools?q=${encodeURIComponent(q)}`);
                                }}
                                className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md"
                                role="search"
                            >
                                <Input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search tools — e.g. ‘pdf’, ‘image’…"
                                    className="h-11 bg-white"
                                    aria-label="Search tools"
                                    data-testid={HOME_PAGE.heroSearch}
                                />
                                <Button
                                    type="submit"
                                    data-testid={HOME_PAGE.heroCta}
                                    className="h-11 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--link-hover))]"
                                >
                                    Browse tools
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </form>
                            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
                                {TRUST_POINTS.map((tp, i) => (
                                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                        <span className="inline-flex w-6 h-6 items-center justify-center rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                                            <tp.icon className="w-3.5 h-3.5" />
                                        </span>
                                        {tp.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="hidden lg:block">
                            <div className="aspect-[4/3] rounded-2xl border border-border/80 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04),0_20px_60px_rgba(15,23,42,0.10)] overflow-hidden">
                                <div className="h-full w-full grid grid-cols-2 grid-rows-2 gap-2 p-3">
                                    {featured.slice(0, 4).map((t) => (
                                        <div
                                            key={t.slug}
                                            className="rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4 flex flex-col justify-between"
                                        >
                                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white text-[hsl(var(--primary))] border border-border/70">
                                                <t.icon className="w-4 h-4" />
                                            </span>
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">{t.title}</p>
                                                <p className="text-xs text-slate-500 line-clamp-1">{t.short}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured tools */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid={HOME_PAGE.featuredTools}>
                <div className="flex items-end justify-between gap-4">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                            Featured tools
                        </h2>
                        <p className="mt-1 text-slate-600">The tools people open every day.</p>
                    </div>
                    <Link to="/tools" className="text-sm text-[hsl(var(--primary))] hover:underline underline-offset-4">
                        See all tools →
                    </Link>
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {featured.map((t) => (
                        <ToolCard key={t.slug} tool={t} />
                    ))}
                    {nextUp.map((t) => (
                        <ToolCard key={t.slug} tool={t} />
                    ))}
                </div>
            </section>

            {/* Category strip */}
            <section className="bg-[hsl(var(--surface-2))] border-y border-border/70" data-testid={HOME_PAGE.categoryGrid}>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="flex items-end justify-between gap-4">
                        <div>
                            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                                Browse by category
                            </h2>
                            <p className="mt-1 text-slate-600">Pick the shelf, and we’ll show you what’s on it.</p>
                        </div>
                    </div>
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                        {CATEGORIES.map((c) => (
                            <Link
                                key={c.id}
                                to={`/tools?category=${c.id}`}
                                className="group rounded-xl border border-border/80 bg-white p-4 hover:border-slate-300 hover:shadow-[0_1px_0_rgba(15,23,42,0.04),0_16px_40px_rgba(15,23,42,0.08)] transition-[border-color,box-shadow] duration-200"
                            >
                                <p className="font-medium text-slate-900">{c.label}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                    {TOOLS.filter((t) => t.category === c.id).length} tools
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Value strip */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <ValueBlock
                        icon={Zap}
                        title="Built to feel instant"
                        body="We host the heavy processing on servers with real horsepower so your phone doesn't have to think about it."
                    />
                    <ValueBlock
                        icon={ShieldCheck}
                        title="Private by default"
                        body="Files are deleted from our servers immediately after processing. We never keep your documents or images."
                    />
                    <ValueBlock
                        icon={Sparkles}
                        title="No signups, no popups"
                        body="Every tool works without an account. Bookmark the ones you use, share them freely with your team."
                    />
                </div>
            </section>

            {/* Latest from blog */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid={HOME_PAGE.latestBlog}>
                <div className="flex items-end justify-between gap-4">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                            Latest from the blog
                        </h2>
                        <p className="mt-1 text-slate-600">Short, opinionated guides on digital productivity.</p>
                    </div>
                    <Link to="/blog" className="text-sm text-[hsl(var(--primary))] hover:underline underline-offset-4">
                        Read the blog →
                    </Link>
                </div>
                {posts.length > 0 ? (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {posts.map((p) => (
                            <BlogCard key={p.slug} post={p} />
                        ))}
                    </div>
                ) : null}
            </section>

            {/* Ad slot — homepage footer strip. Only renders when AdSense is enabled + slot id set. */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
                <AdSlot placement="homepage" />
            </section>
        </>
    );
}

function ValueBlock({ icon: Icon, title, body }) {
    return (
        <div className="rounded-xl border border-border/70 bg-white p-5">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                <Icon className="w-5 h-5" />
            </span>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">{title}</h3>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
        </div>
    );
}
